import { z } from 'zod';
import { currencyCodeSchema, moneySchema, rateSchema } from '../common/primitives.js';
import { phoneSchema, storedPhoneSchema } from '../phone/phone.js';
import { otpCodeSchema } from '../phone/verification.js';
// ---------------------------------------------------------------------------
// Currencies (العملات) — currency.md section 3.3.
// ---------------------------------------------------------------------------
export const currencyStatusSchema = z.enum(['ACTIVE', 'INACTIVE']);
export const currencyDrawerSchema = z
    .object({
    code: currencyCodeSchema,
    nameAr: z.string().trim().min(1, 'nameAr is required'),
    nameEn: z.string().trim().min(1, 'nameEn is required'),
    buy: rateSchema,
    sell: rateSchema,
    vipEnabled: z.boolean().default(false),
    vipBuy: rateSchema.optional(),
    vipSell: rateSchema.optional(),
})
    .refine((v) => Number(v.sell) >= Number(v.buy), {
    message: 'sell price must be greater than or equal to the buy price',
    path: ['sell'],
})
    .refine((v) => !v.vipEnabled || (v.vipBuy !== undefined && v.vipSell !== undefined), {
    message: 'VIP buy and sell prices are required when VIP is enabled',
    path: ['vipBuy'],
})
    .refine((v) => !v.vipEnabled ||
    v.vipBuy === undefined ||
    v.vipSell === undefined ||
    Number(v.vipSell) >= Number(v.vipBuy), {
    message: 'VIP sell price must be greater than or equal to the VIP buy price',
    path: ['vipSell'],
});
export const currencyChangeStatusSchema = z.object({ status: currencyStatusSchema });
// ---------------------------------------------------------------------------
// Exchange (عملية صرف) — currency.md section 3.1.
// ---------------------------------------------------------------------------
export const exchangePreviewSchema = z
    .object({
    holderId: z.string().min(1),
    receivedCurrencyId: z.string().min(1),
    receivedAmount: moneySchema.optional(),
    paidCurrencyId: z.string().min(1),
    paidAmount: moneySchema.optional(),
    vip: z.boolean().default(false),
})
    .refine((v) => v.receivedAmount !== undefined || v.paidAmount !== undefined, {
    message: 'either receivedAmount or paidAmount is required',
    path: ['receivedAmount'],
});
/**
 * The exchange app's handle on a tablet task: the tablet service's task id
 * plus a server-issued tag binding it to the cashier who sent it. Opaque to
 * the client.
 */
export const tabletRequestIdSchema = z
    .string()
    .min(1)
    .max(300)
    .regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/, 'must be a tablet request id');
export const exchangeCreateSchema = z
    .object({
    holderId: z.string().min(1),
    receivedCurrencyId: z.string().min(1),
    receivedAmount: moneySchema,
    paidCurrencyId: z.string().min(1),
    vip: z.boolean().default(false),
    customerPhone: phoneSchema,
    customerName: z.string().trim().optional(),
    notes: z.string().trim().max(2000).optional(),
    /** An ExchangeImage id from POST /exchanges/images (exchange app photo). */
    imageId: z.string().min(1).optional(),
    /**
     * The signed tablet request (currency.md section 3.1.1), as returned by
     * POST /exchange-app/tablet-requests. The server collects the signature
     * and stores it with the exchange in one step, or posts nothing.
     */
    tabletRequestId: tabletRequestIdSchema.optional(),
    /**
     * العميل غير موجود (KAN-33, currency.md section 3.1.1): the exchange is
     * saved without a signature. Never combined with `tabletRequestId`.
     */
    customerNotPresent: z.boolean().default(false),
    /**
     * The verified WhatsApp phone verification (customers.md section 10,
     * currency.md section 3.1). Required while إعدادات الصرف has the switch on
     * and the phone is not verified in Customers; the server consumes it
     * before the exchange commits.
     */
    phoneVerificationId: z.string().trim().min(1).max(64).optional(),
})
    .refine((v) => !(v.customerNotPresent && v.tabletRequestId), {
    message: 'a customer who is not present cannot sign',
    path: ['customerNotPresent'],
})
    // العميل غير موجود asks no code (customers.md section 10.2), so it never
    // carries a verification and the API never consumes one for it.
    .superRefine((v, ctx) => {
    if (v.customerNotPresent && v.phoneVerificationId) {
        ctx.addIssue({
            code: 'custom',
            path: ['phoneVerificationId'],
            message: 'PHONE_VERIFICATION_NOT_ALLOWED',
            params: { code: 'PHONE_VERIFICATION_NOT_ALLOWED' },
        });
    }
});
// ---------------------------------------------------------------------------
// Exchange app (أنظمة صرف العملات) — currency.md sections 1.1 and 3.1.
// ---------------------------------------------------------------------------
/** The customer photo: legacy `uploadImage`, re-specified (currency.md section 3.1). */
export const EXCHANGE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const EXCHANGE_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
/** GET /exchange-app/context?vip= — the legacy `currencies/getCurrencies?isVip`. */
export const exchangeContextQuerySchema = z.object({
    vip: z.stringbool().default(false),
});
export const exchangeContextCurrencySchema = z.object({
    id: z.string(),
    code: z.string(),
    nameAr: z.string(),
    nameEn: z.string(),
    /** سعر الشراء shown in the من panel (VIP price on the VIP screen). */
    buy: z.string(),
    /** سعر البيع shown in the إلى panel (VIP price on the VIP screen). */
    sell: z.string(),
    /** تاريخ آخر تعديل للعملات: ISO-8601 of the currency's last change. */
    updatedAt: z.string(),
});
export const exchangeContextSchema = z.object({
    vip: z.boolean(),
    /** The chosen box (section 3.1.4), or null when the user has no active box. */
    box: z.object({ id: z.string(), branchId: z.string().nullable() }).nullable(),
    currencies: z.array(exchangeContextCurrencySchema),
    /**
     * The switch تأكيد رقم الهاتف برمز واتساب of إعدادات الصرف (currency.md
     * section 3.7): when on, متابعة opens the verification dialog for a phone
     * not verified in Customers.
     */
    phoneVerificationEnabled: z.boolean(),
});
/**
 * GET /boxes/me: the chosen box with its balance per currency, shown on the
 * exchange app's رصيد الصندوق page (currency.md section 3.1). Null when the
 * user has no active box.
 */
export const myBoxSchema = z
    .object({
    id: z.string(),
    /** The box name (KAN-55), shown above the balances. */
    name: z.string().nullable(),
    branchId: z.string().nullable(),
    balances: z.array(z.object({
        currencyId: z.string(),
        code: z.string(),
        nameAr: z.string(),
        nameEn: z.string(),
        amount: z.string(),
    })),
})
    .nullable();
/**
 * The header carrying the box chosen in the exchange app (currency.md section
 * 3.1.4) on every call that reads or changes a box without a holderId in its
 * body.
 */
export const BOX_ID_HEADER = 'x-box-id';
/** GET /exchange-app/boxes: one active box the user works in (اختيار الصندوق). */
export const myActiveBoxSchema = z.object({
    id: z.string(),
    name: z.string(),
    branchId: z.string(),
    branchName: z.object({ ar: z.string(), en: z.string() }).nullable(),
});
export const myActiveBoxesSchema = z.object({ data: z.array(myActiveBoxSchema) });
/** POST /exchanges/images response. */
export const exchangeImageUploadSchema = z.object({ id: z.string() });
// ---------------------------------------------------------------------------
// Customer signature on the shared tablet — currency.md section 3.1.1.
// ---------------------------------------------------------------------------
export const tabletSchema = z.object({
    id: z.string(),
    name: z.string(),
    online: z.boolean(),
    busy: z.boolean(),
});
/**
 * GET /exchange-app/tablets: the tablets of the cashier's branch. `enabled`
 * is false when the tablet service is not configured; the exchange app then
 * hides the signature section, as it does for an empty list.
 */
export const exchangeTabletsSchema = z.object({
    enabled: z.boolean(),
    tablets: z.array(tabletSchema),
});
/**
 * POST /exchange-app/tablet-requests: the exchange as the summary shows it.
 * The server recalculates the figures and builds the tablet task itself.
 */
export const tabletRequestCreateSchema = z.object({
    tabletId: z.string().min(1).max(100),
    /** Stable per summary visit, so a repeated click returns the same task. */
    draftKey: z.string().min(8).max(100),
    holderId: z.string().min(1),
    receivedCurrencyId: z.string().min(1),
    receivedAmount: moneySchema,
    paidCurrencyId: z.string().min(1),
    vip: z.boolean().default(false),
    customerPhone: phoneSchema,
    customerName: z.string().trim().max(120).optional(),
});
export const TABLET_REQUEST_STATUSES = [
    'SENT',
    'OPENED',
    'SUBMITTED',
    'CANCELLED',
    'EXPIRED',
];
export const tabletRequestStatusSchema = z.enum(TABLET_REQUEST_STATUSES);
export const tabletRequestSchema = z.object({
    requestId: tabletRequestIdSchema,
    status: tabletRequestStatusSchema,
    tabletOnline: z.boolean(),
    /** Present once the customer pressed تم. */
    submission: z
        .object({
        name: z.string().nullable(),
        phone: z.string().nullable(),
        language: z.enum(['ar', 'en']),
        signedAt: z.string(),
    })
        .nullable(),
});
/** The signature stored with a posted exchange (currency.md sections 3.1.1, 3.2). */
export const exchangeSignatureSchema = z.object({
    signedAt: z.string(),
    signedName: z.string().nullable(),
    signedPhone: z.string(),
    language: z.enum(['ar', 'en']),
});
export const exchangeReverseSchema = z.object({
    reason: z.string().trim().min(1, 'a reversal reason is required'),
});
export const exchangeListQuerySchema = z.object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(25),
    dateFrom: z.iso.datetime({ offset: true }).optional(),
    dateTo: z.iso.datetime({ offset: true }).optional(),
    branchId: z.string().optional(),
    cashierId: z.string().optional(),
    /** The box (KAN-55, currency.md section 3.2). */
    holderId: z.string().optional(),
    currencyIn: z.string().optional(),
    currencyOut: z.string().optional(),
    status: z.enum(['POSTED', 'REVERSED']).optional(),
    /** Tokenized smart search over customer name/phone/reference (search_text). */
    search: z.string().trim().optional(),
});
// ---------------------------------------------------------------------------
// Treasury (الخزينة) — currency.md section 3.4.
// ---------------------------------------------------------------------------
export const cashLineSchema = z.object({
    currencyId: z.string().min(1),
    amount: moneySchema,
});
export const centralFeedSchema = z.object({
    type: z.enum(['OPENING', 'FEED']),
    lines: z.array(cashLineSchema).min(1, 'at least one currency line is required'),
    notes: z.string().trim().max(2000).optional(),
});
export const cashMoveSchema = z.object({
    fromHolderId: z.string().min(1),
    toHolderId: z.string().min(1),
    lines: z.array(cashLineSchema).min(1, 'at least one currency line is required'),
    notes: z.string().trim().max(2000).optional(),
});
export const countLineSchema = z.object({
    currencyId: z.string().min(1),
    counted: moneySchema,
});
export const countPreviewSchema = z.object({ holderId: z.string().min(1) });
export const countCreateSchema = z.object({
    holderId: z.string().min(1),
    lines: z.array(countLineSchema).min(1, 'at least one currency line is required'),
    notes: z.string().trim().max(2000).optional(),
});
// ---------------------------------------------------------------------------
// Boxes (الصناديق) — currency.md section 3.2.1, owner tickets KAN-56 and KAN-55.
// ---------------------------------------------------------------------------
export const BOX_NAME_MAX = 100;
const boxNameSchema = z
    .string()
    .trim()
    .min(1, 'the box name is required')
    .max(BOX_NAME_MAX, 'the box name is too long');
const boxEmployeeIdsSchema = z
    .array(z.string().min(1))
    .min(1, 'choose at least one employee')
    .max(50)
    .refine((ids) => new Set(ids).size === ids.length, 'an employee is listed twice');
export const boxCreateSchema = z.object({
    name: boxNameSchema,
    branchId: z.string().min(1, 'the branch is required'),
    employeeIds: boxEmployeeIdsSchema,
});
/** The branch is fixed after creation (section 3.2.1). */
export const boxUpdateSchema = z.object({
    name: boxNameSchema,
    employeeIds: boxEmployeeIdsSchema,
});
/** GET /boxes/eligible-employees: the box drawer's الموظفون options. */
export const boxEligibleEmployeesQuerySchema = z.object({
    branchId: z.string().min(1),
    /** In تعديل صندوق, the box's own employees are listed too. */
    holderId: z.string().optional(),
});
/** PATCH /boxes/:id/status (FUND_CHANGE_STATUS, decision 18). */
export const boxChangeStatusSchema = z.object({ status: z.enum(['ACTIVE', 'INACTIVE']) });
export const boxEmployeeSchema = z.object({
    id: z.string(),
    nameAr: z.string(),
    nameEn: z.string(),
    username: z.string(),
});
// ---------------------------------------------------------------------------
// Cash receipts and payments (المقبوضات والمدفوعات) in the exchange app —
// currency.md section 3.1.3, owner ticket KAN-57.
// ---------------------------------------------------------------------------
export const CASH_ENTRY_TYPES = ['RECEIPT', 'PAYMENT'];
export const cashEntryTypeSchema = z.enum(CASH_ENTRY_TYPES);
export const CASH_ENTRY_DESCRIPTION_MAX = 500;
/** True for a decimal string greater than zero ("0.00001" yes, "0", "-1" no). */
function isPositiveDecimal(value) {
    return !value.startsWith('-') && /[1-9]/.test(value);
}
export const cashEntryCreateSchema = z.object({
    type: cashEntryTypeSchema,
    /** The box chosen in the exchange app (section 3.1.4). */
    holderId: z.string().min(1),
    currencyId: z.string().min(1, 'the currency is required'),
    amount: moneySchema.refine(isPositiveDecimal, {
        message: 'the amount must be greater than zero',
    }),
    description: z
        .string()
        .trim()
        .min(1, 'the description is required')
        .max(CASH_ENTRY_DESCRIPTION_MAX, 'the description is too long'),
});
export const cashEntryListQuerySchema = z.object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(25),
    type: cashEntryTypeSchema.optional(),
});
export const cashEntrySchema = z.object({
    id: z.string(),
    number: z.number().int(),
    type: cashEntryTypeSchema,
    holderId: z.string(),
    boxName: z.string().nullable(),
    cashierId: z.string(),
    cashierName: z.string(),
    currencyId: z.string(),
    currencyCode: z.string(),
    amount: z.string(),
    description: z.string(),
    createdAt: z.string(),
});
export const cashEntryListSchema = z.object({
    data: z.array(cashEntrySchema),
    meta: z.object({
        page: z.number(),
        pageSize: z.number(),
        total: z.number(),
        totalPages: z.number(),
    }),
});
// ---------------------------------------------------------------------------
// Reports — currency.md section 3.5.
// ---------------------------------------------------------------------------
export const reportSummaryQuerySchema = z.object({
    dateFrom: z.iso.datetime({ offset: true }),
    dateTo: z.iso.datetime({ offset: true }),
    branchId: z.string().optional(),
    groupBy: z.enum(['week', 'month', 'year']).default('month'),
});
export const reportExportTableSchema = z.enum([
    'box-performance',
    'cashier-performance',
    'currency-performance',
    'cash-moves',
    'box-balances',
    'vault-balances',
    'differences',
]);
export const reportExportQuerySchema = reportSummaryQuerySchema.extend({
    table: reportExportTableSchema,
});
// ---------------------------------------------------------------------------
// Settings (إعدادات الصرف) — currency.md section 3.7.
// ---------------------------------------------------------------------------
/** GET /settings: the dashboard's only settings page. */
export const currencySettingsSchema = z.object({
    /** تأكيد رقم الهاتف برمز واتساب, off by default (customers.md section 10.1). */
    phoneVerificationEnabled: z.boolean(),
    updatedAt: z.string().nullable(),
});
/** PATCH /settings (SETTINGS_UPDATE, audited). */
export const updateCurrencySettingsSchema = z.object({
    phoneVerificationEnabled: z.boolean(),
});
// ---------------------------------------------------------------------------
// WhatsApp phone verification in the exchange app (customers.md section 10,
// currency.md section 3.1). The Currency API forwards these to Customers with
// the acting cashier; Currency only ever asks for purpose CREATE.
// ---------------------------------------------------------------------------
/** POST /exchange-app/phone-verifications (Idempotency-Key required). */
export const exchangePhoneVerificationRequestSchema = z.object({
    phone: phoneSchema,
    language: z.enum(['ar', 'en']).default('ar'),
});
/** POST /exchange-app/phone-verifications/{id}/verify. */
export const exchangePhoneVerificationVerifySchema = z.object({ code: otpCodeSchema });
/**
 * GET /customers/lookup?phone=: normalised with the shared phone rule. A
 * stored phone of an excluded country is still looked up (customers.md
 * section 9.7); a new exchange with it is refused by `exchangeCreateSchema`.
 */
export const customerLookupQuerySchema = z.object({ phone: storedPhoneSchema });
//# sourceMappingURL=contract.js.map